import _ from 'lodash';

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { Person } from 'src/app/people/models/person';
import { EmailType, IEmailMessageWrite } from 'src/app/shared/models/emailMessage';
import { EmailCustomization } from '../../models/emailCustomization';
import {
  EmailPreparationService,
  EmailPreparation
} from 'src/app/shared/services/email-preparation.service';

@Component({
  selector: 'send-email',
  templateUrl: 'send-email.component.html',
  styleUrls: ['send-email.component.scss'],
  standalone: false
})
export class SendEmailComponent implements OnInit {
  static readonly EmailTemplateFormControlName: string = 'emailTemplate';
  static readonly FromNameFormControlName: string = 'fromName';
  static readonly ReplyToFormControlName: string = 'replyTo';
  static readonly SubjectFormControlName: string = 'subject';
  static readonly ContentFormControlName: string = 'content';

  emailType: EmailType;
  title: string;
  templateName: string;
  footer: string;
  recipientPersonId: string;
  isRecipientInvalid: boolean;
  credentialCoverageWarning: string | null;
  emailForm: UntypedFormGroup;
  emailCustomization?: EmailCustomization;
  sendButtonText?: string;
  cancelButtonText?: string;

  private emailSub: Subscription;
  private person?: Person;
  private peopleIds?: string[];
  private lastCheckedContent: string;

  constructor(
    private emailPreparationService: EmailPreparationService,
    public authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.emailSub = this.emailPreparationService.emailSent.subscribe(() => {
      this.close();
    });
    const data = history?.state?.data as EmailPreparation;
    this.sendButtonText = data.sendButtonText || 'Send Email now';
    this.cancelButtonText = data.cancelButtonText || 'Cancel';
    if (data) {
      this.emailType = data.emailType;
      this.title = data.title;
      this.templateName = data.templateName;
      this.person = data.person;
      this.recipientPersonId = this.person ? this.person.id : '';
      this.peopleIds = data.peopleIds;

      this.emailPreparationService.isRecipientInvalid(data).subscribe((isInvalid) => {
        this.isRecipientInvalid = isInvalid;
        this.footer = this.emailPreparationService.prepareFooter(data, this.isRecipientInvalid);
      });

      this.emailCustomization = data.emailCustomization;
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
      return;
    }
  }

  isValid(): boolean {
    if (!this.emailForm || this.isRecipientInvalid) return false;
    return this.emailForm.valid;
  }

  onComposeEmailComplete(form: UntypedFormGroup) {
    this.emailForm = form;
    this.updateCredentialCoverageWarning();
  }

  private async updateCredentialCoverageWarning() {
    const content = this.emailForm?.get(SendEmailComponent.ContentFormControlName)?.value;
    const subject = this.emailForm?.get(SendEmailComponent.SubjectFormControlName)?.value;
    const checkedSource = `${subject ?? ''} ${content ?? ''}`.trim();
    if (!this.peopleIds?.length || !checkedSource) {
      this.credentialCoverageWarning = null;
      return;
    }
    if (checkedSource === this.lastCheckedContent) {
      return;
    }
    this.lastCheckedContent = checkedSource;
    const warning = await this.emailPreparationService.getCredentialCoverageWarning(
      this.peopleIds,
      checkedSource
    );
    if (this.lastCheckedContent === checkedSource) {
      this.credentialCoverageWarning = warning;
    }
  }

  send() {
    if (this.isValid()) {
      var message: IEmailMessageWrite = {
        subject: this.emailForm.get(SendEmailComponent.SubjectFormControlName).value,
        senderName: this.emailForm.get(SendEmailComponent.FromNameFormControlName).value,
        replyTo: this.emailForm.get(SendEmailComponent.ReplyToFormControlName).value,
        email: this.emailForm.get(SendEmailComponent.ContentFormControlName).value
      };
      this.emailPreparationService.onEmailPrepared({
        emailType: this.emailType,
        emailMessage: message
      });
    }
  }

  close() {
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  ngOnDestroy() {
    if (this.emailSub) {
      this.emailSub.unsubscribe();
    }
  }
}
